import {
  ampPathForRoute,
  canonicalRoutePathFromLocalizedPath,
  localizedPathForRoute,
} from "@/lib/seo/localized-slugs";
import { canonicalRoutePathForInternalLink } from "@/lib/routes/registry";

const CANONICAL_ORIGIN = "https://seoh.team";
const CANONICAL_HOSTS = new Set(["seoh.team", "www.seoh.team", "seoh.co", "www.seoh.co"]);

export function getCanonicalOrigin(rawOrigin = process.env.NEXT_PUBLIC_SITE_URL): string {
  try {
    const url = new URL(rawOrigin?.trim() || CANONICAL_ORIGIN);

    if (CANONICAL_HOSTS.has(url.hostname.toLowerCase())) {
      return CANONICAL_ORIGIN;
    }

    return url.origin;
  } catch {
    return CANONICAL_ORIGIN;
  }
}

export function getMetadataBase(): URL {
  return new URL(getCanonicalOrigin());
}

export function normalizePagePath(path = "/"): string {
  const trimmed = path.trim() || "/";
  const pathname = pathnameFromInput(trimmed);
  const prefixed = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const withoutTrailingSlash = prefixed.length > 1 ? prefixed.replace(/\/+$/, "") : prefixed;

  return withoutTrailingSlash || "/";
}

export function normalizeCanonicalPagePath(path = "/"): string {
  const normalizedPath = normalizePagePath(path);

  return canonicalRoutePathForInternalLink(canonicalRoutePathFromLocalizedPath(normalizedPath));
}

export function toCanonicalUrl(path = "/", origin = getCanonicalOrigin()): string {
  return new URL(normalizePagePath(path), getCanonicalOrigin(origin)).href;
}

export function localizedPath(locale: string, path = "/"): string {
  return localizedPathForRoute(locale, normalizeCanonicalPagePath(path));
}

export function ampPath(locale: string, path = "/"): string {
  return ampPathForRoute(locale, normalizeCanonicalPagePath(path));
}

export function toAmpUrl(locale: string, path = "/", origin = getCanonicalOrigin()): string {
  return new URL(ampPath(locale, path), getCanonicalOrigin(origin)).href;
}

function pathnameFromInput(value: string): string {
  try {
    return new URL(value).pathname;
  } catch {
    return value.split(/[?#]/, 1)[0] || "/";
  }
}
